import { useRef, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap';
import { useReducedMotion } from '@/scroll/use-reduced-motion';

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

/** Hermite ease between two edges, as in the React Bits original. */
const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = clamp01((x - edge0) / (edge1 - edge0 || 1e-6));
  return t * t * (3 - 2 * t);
};

export interface ScrollExpandProps {
  /**
   * ScrollTrigger id, normally the section id: the pin start is the exact top of the section,
   * which is what `scrollToSection` looks for (a pinned section's bounding rect is not).
   */
  triggerId?: string;
  /**
   * Layer under the frame, filling the stage: the ground the resting frame sits on (glows,
   * vignette). It fades out as the frame opens, since at full bleed the media covers it.
   */
  backdrop?: ReactNode;
  /**
   * Layer the frame clips, e.g. a shader canvas. It fills the stage and never resizes. The
   * stage carries `--expand` (0 at rest, 1 at full bleed) for CSS inside it to react to.
   */
  media: ReactNode;
  /**
   * Layer over the frame that the clip does not cut, for ornament that rides outside the
   * resting frame (the coins orbit). It reads `--expand` like the media and takes no pointer.
   */
  ornament?: ReactNode;
  /** Optional layer over the media whose opacity rises with the expansion, for legibility. */
  scrim?: ReactNode;
  /** Held over the resting frame; lifts away as the media takes the stage. */
  resting?: ReactNode;
  /** Small cue under the resting frame; leaves as soon as the scroll begins. */
  hint?: ReactNode;
  /** Content that fades in over the media once the frame reaches full bleed. */
  children?: ReactNode;
  /**
   * `frame`: a rounded rectangle sized by `startWidth` x `startHeight`. `circle`: a disc of
   * `startSize` diameter that grows until it covers the stage's corners.
   */
  shape?: 'frame' | 'circle';
  /** Frame width before expanding, as a percentage of the stage (`frame` only). */
  startWidth?: number;
  /** Frame height before expanding, as a percentage of the stage (`frame` only). */
  startHeight?: number;
  /** Disc diameter before expanding, as a percentage of the stage's shorter side (`circle`). */
  startSize?: number;
  /** Corner radius of the resting frame, in px (`frame` only). */
  startRadius?: number;
  /** Corner radius once fully expanded, in px (`frame` only). */
  endRadius?: number;
  /** Media scale at rest, easing back to 1 as the frame opens. 1 leaves the media untouched. */
  mediaZoom?: number;
  /** Scroll length of the expansion, in multiples of the stage height. */
  scrollDistance?: number;
  /** Extra scroll the frame stays pinned at full bleed before releasing. */
  holdDistance?: number;
  /** Opacity the scrim reaches at full bleed. */
  scrimOpacity?: number;
  className?: string;
}

/**
 * React Bits' ScrollExpand: a frame resting at card size (or, with `shape="circle"`, a disc)
 * opens to full bleed as the page scrolls, then holds while the content over it fades in.
 * Ported to this stack rather than dropped in: the original's own scroll listener and rAF
 * lerp are replaced by a pinned, scrubbed ScrollTrigger, so Lenis drives the motion and a
 * single ticker runs the page.
 *
 * All of the moving styles are written by GSAP inside `useGSAP`, including the resting start
 * state (a layout effect, before first paint), so nothing flashes and everything reverts on
 * unmount. Under reduced motion nothing is pinned and no scroll length is added: the frame is
 * left open by CSS and `resting` and `children` stack down the page, since without the
 * expansion the two of them can no longer take turns on one screen.
 *
 * Reveal targets (`[data-line]`, `[data-reveal]`) belong in `resting` and `hint`, which the
 * section's own reveal timeline plays; `children` are already staged by the scrub, so they
 * must not carry those attributes or they would be hidden and never brought back.
 */
export function ScrollExpand({
  triggerId,
  backdrop,
  media,
  ornament,
  scrim,
  resting,
  hint,
  children,
  shape = 'frame',
  startWidth = 42,
  startHeight = 58,
  startSize = 64,
  startRadius = 24,
  endRadius = 0,
  mediaZoom = 1,
  scrollDistance = 1.2,
  holdDistance = 0.35,
  scrimOpacity = 1,
  className,
}: ScrollExpandProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const restingRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  /** Reduced motion gets no expansion, so the two blocks of copy cannot share one screen. */
  const stacked = reduced;

  useGSAP(
    () => {
      const root = rootRef.current;
      const stage = stageRef.current;
      const frame = frameRef.current;
      // Stacked (reduced motion): the frame is already open in CSS and both blocks of copy sit
      // in normal flow, so there is nothing to drive and no scroll length to add.
      if (!root || !stage || !frame || reduced) return;

      let overlayLive: boolean | null = null;

      /** `p` is the expansion alone, 0..1; the hold runs on after it has reached 1. */
      const apply = (p: number) => {
        const e = smoothstep(0, 1, p);

        if (shape === 'circle') {
          // From the resting disc to a radius of half the stage diagonal, which clears the
          // corners; the stage is measured live so a resize mid-scrub still covers them.
          const w = stage.offsetWidth;
          const h = stage.offsetHeight;
          const r0 = (Math.min(w, h) * startSize) / 200;
          const r1 = Math.hypot(w, h) / 2;
          gsap.set(frame, { clipPath: `circle(${r0 + (r1 - r0) * e}px at 50% 50%)` });
        } else {
          const inlineInset = Math.max(0, (100 - (startWidth + (100 - startWidth) * e)) / 2);
          const blockInset = Math.max(0, (100 - (startHeight + (100 - startHeight) * e)) / 2);
          const radius = startRadius + (endRadius - startRadius) * e;
          gsap.set(frame, {
            clipPath: `inset(${blockInset}% ${inlineInset}% ${blockInset}% ${inlineInset}% round ${radius}px)`,
          });
        }

        // `--expand` is the stage's own read on the opening: 0 at rest, 1 once the frame is
        // at full bleed, on a curve that finishes just as the overlay begins to arrive, so
        // ornament that would sit under the copy (the coins orbit) can recede in CSS.
        stage.style.setProperty('--expand', smoothstep(0.4, 0.85, p).toFixed(4));
        if (mediaRef.current && mediaZoom !== 1) {
          gsap.set(mediaRef.current, { scale: mediaZoom + (1 - mediaZoom) * e });
        }
        if (scrimRef.current) gsap.set(scrimRef.current, { opacity: scrimOpacity * e });
        if (backdropRef.current) {
          // Gone before the corners square off, so no halo lingers in the last gaps.
          gsap.set(backdropRef.current, { opacity: 1 - smoothstep(0.3, 0.8, p) });
        }

        if (restingRef.current) {
          // Out well before the overlay comes in. The React Bits curves (0.4..0.88 against
          // 0.68..1) leave both blocks of copy half visible over each other for a fifth of the
          // expansion, which reads as one double-exposed headline.
          const out = smoothstep(0.32, 0.64, p);
          gsap.set(restingRef.current, { opacity: 1 - out, y: -28 * out, scale: 1 + 0.06 * out });
        }
        if (hintRef.current) {
          const gone = smoothstep(0, 0.12, p);
          gsap.set(hintRef.current, { opacity: 1 - gone, y: 8 * gone });
        }
        if (overlayRef.current) {
          const inn = smoothstep(0.72, 1, p);
          gsap.set(overlayRef.current, { opacity: inn, y: 18 * (1 - inn) });
          // The overlay holds links: while it is still fading in it must not take a click or
          // the keyboard focus, so it is inert until it is essentially opaque.
          const live = inn > 0.9;
          if (live !== overlayLive) {
            overlayLive = live;
            overlayRef.current.toggleAttribute('inert', !live);
          }
        }
      };

      apply(0);

      // self.progress spans expansion + hold; the frame is done expanding before the hold.
      const span = (scrollDistance + holdDistance) / Math.max(0.01, scrollDistance);
      const sync = (self: ScrollTrigger) => apply(clamp01(self.progress * span));

      ScrollTrigger.create({
        id: triggerId,
        trigger: root,
        start: 'top top',
        end: () => `+=${stage.offsetHeight * (scrollDistance + holdDistance)}`,
        pin: stage,
        anticipatePin: 1,
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: sync,
        onRefresh: sync,
      });
    },
    {
      scope: rootRef,
      dependencies: [
        reduced,
        shape,
        startWidth,
        startHeight,
        startSize,
        startRadius,
        endRadius,
        mediaZoom,
        scrollDistance,
        holdDistance,
        scrimOpacity,
        triggerId,
      ],
      revertOnUpdate: true,
    },
  );

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      {/* Stacked, the frame is open and the copy sits over the media, so the media reads as
          fully expanded; React drops the property again when the scrub takes over. */}
      <div
        ref={stageRef}
        className={cn(
          'relative w-full overflow-hidden',
          stacked ? 'min-h-svh py-[clamp(7rem,16vh,10rem)]' : 'h-svh',
        )}
        style={stacked ? ({ '--expand': 1 } as CSSProperties) : undefined}
      >
        {backdrop && !stacked ? (
          <div ref={backdropRef} className="pointer-events-none absolute inset-0">
            {backdrop}
          </div>
        ) : null}
        <div
          ref={frameRef}
          className={cn('absolute inset-0', !stacked && '[will-change:clip-path]')}
        >
          <div ref={mediaRef} className="absolute inset-0 origin-center">
            {media}
          </div>
          {scrim ? (
            <div ref={scrimRef} className="pointer-events-none absolute inset-0">
              {scrim}
            </div>
          ) : null}
          {children && !stacked ? (
            <div
              ref={overlayRef}
              className="absolute inset-0 grid place-items-center px-[clamp(1.4rem,5vw,5rem)] pb-[clamp(2.5rem,8vh,5rem)] pt-[clamp(5rem,12vh,7rem)]"
            >
              {children}
            </div>
          ) : null}
        </div>
        {ornament ? <div className="pointer-events-none absolute inset-0">{ornament}</div> : null}

        {stacked ? (
          <div className="relative grid justify-items-center gap-[clamp(3rem,8vh,5rem)] px-[clamp(1.4rem,5vw,5rem)]">
            {resting}
            {children}
          </div>
        ) : (
          <>
            {resting ? (
              <div
                ref={restingRef}
                className="pointer-events-none absolute inset-0 grid place-items-center px-[clamp(1.4rem,5vw,5rem)]"
              >
                {resting}
              </div>
            ) : null}
            {hint ? (
              <div
                ref={hintRef}
                className="pointer-events-none absolute inset-x-0 bottom-8 hidden justify-center md:flex"
              >
                {hint}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
